import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { toast } from 'sonner';
import { Upload, ArrowLeft, Loader2 } from 'lucide-react';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const { settings } = useSettings();
    const navigate = useNavigate();

    const businessName = settings?.business_name || 'BGRemove';
    const logoUrl = settings?.logo_url;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            toast.error('Please fill in all fields');
            return;
        }
        
        setLoading(true);
        try {
            await login(email, password);
            toast.success('Welcome back!');
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Invalid credentials');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex">
            {/* Grain overlay */}
            <div className="grain-overlay" />
            
            {/* Left side - Brand */}
            <div className="hidden lg:flex lg:w-1/2 bg-foreground text-background p-12 flex-col justify-between">
                <Link to="/" className="flex items-center gap-2" data-testid="login-logo-link">
                    {logoUrl ? (
                        <img 
                            src={logoUrl.startsWith('http') ? logoUrl : `${process.env.REACT_APP_BACKEND_URL}${logoUrl}`}
                            alt={businessName}
                            className="w-8 h-8 rounded-lg object-contain"
                        />
                    ) : (
                        <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                            <Upload className="w-4 h-4 text-primary-foreground" />
                        </div>
                    )}
                    <span className="font-heading font-bold text-xl tracking-tight">{businessName}</span>
                </Link>
                
                <div>
                    <h2 className="font-heading font-bold text-4xl tracking-tight mb-4">
                        Welcome Back
                    </h2>
                    <p className="text-background/70 text-lg">
                        Continue removing backgrounds with AI precision.
                    </p>
                </div>

                <p className="text-sm text-background/50">
                    © 2024 {businessName}. All rights reserved.
                </p>
            </div>

            {/* Right side - Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
                <div className="w-full max-w-md">
                    <Link 
                        to="/" 
                        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
                        data-testid="login-back-link"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to home
                    </Link>

                    <div className="lg:hidden mb-8">
                        <Link to="/" className="flex items-center gap-2" data-testid="login-mobile-logo-link">
                            {logoUrl ? (
                                <img 
                                    src={logoUrl.startsWith('http') ? logoUrl : `${process.env.REACT_APP_BACKEND_URL}${logoUrl}`}
                                    alt={businessName}
                                    className="w-8 h-8 rounded-lg object-contain"
                                />
                            ) : (
                                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                                    <Upload className="w-4 h-4 text-primary-foreground" />
                                </div>
                            )}
                            <span className="font-heading font-bold text-xl tracking-tight">{businessName}</span>
                        </Link>
                    </div>

                    <h1 className="font-heading font-bold text-3xl tracking-tight mb-2">
                        Sign in
                    </h1>
                    <p className="text-muted-foreground mb-8">
                        Enter your credentials to access your account
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="h-12"
                                data-testid="login-email-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="password">Password</Label>
                                <Link
                                    to="/forgot-password"
                                    className="text-sm text-primary hover:underline"
                                    data-testid="forgot-password-link"
                                >
                                    Forgot password?
                                </Link>
                            </div>
                            <Input
                                id="password"
                                type="password"
                                placeholder="Enter your password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="h-12"
                                data-testid="login-password-input"
                            />
                        </div>

                        <Button 
                            type="submit" 
                            className="w-full h-12 rounded-full text-base"
                            disabled={loading}
                            data-testid="login-submit-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Signing in...
                                </>
                            ) : (
                                'Sign in'
                            )}
                        </Button>
                    </form>

                    <p className="text-center text-sm text-muted-foreground mt-8">
                        Don't have an account?{' '}
                        <Link 
                            to="/register" 
                            className="text-primary font-medium hover:underline"
                            data-testid="login-register-link"
                        >
                            Sign up free
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;
